import React, { useState } from "react";
import {
    Box,
    Button,
    Stack,
    Typography,
    CssBaseline,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Paper,
    Avatar,
    CircularProgress,
} from "@mui/material";
import { createTheme, ThemeProvider } from '@mui/material';
import getCustomTheme from "../../Theme/CustomTheme";
import axios from '../../Configs/axiosInstance';
import { MAIN_URL } from "../../Configs/Urls";

import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import NotesRoundedIcon from '@mui/icons-material/NotesRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PatientFormFields, { emptyPatientForm, validatePatientForm } from "./Components/PatientFormFields";

export default function PatientAddFormOpen() {

    const [openDialog, setOpenDialog] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false); // For Snackbar visibility
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // For Snackbar severity

    const customTheme = createTheme(getCustomTheme('light'));

    const [patientData, setPatientData] = useState(emptyPatientForm);
    const [errors, setErrors] = useState({});

    const handleInputChange = (event) => {
        setErrors({})
        const { name, value } = event.target;
        setPatientData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    // Used by the ChoiceField pill-buttons (single-select fields that used
    // to be RadioGroups: dietaryPreference, alcohol, gender, lifestyle).
    const setField = (field) => (value) => {
        setErrors({});
        setPatientData((prevData) => ({
            ...prevData,
            [field]: value,
        }));
    };

    // Used by the MultiChoiceField pill-buttons (multi-select fields that
    // used to be checkbox stacks: exerciseRegime, physiologicalConditions).
    const setMultiField = (field) => (values) => {
        setErrors({});
        setPatientData((prevData) => ({
            ...prevData,
            [field]: values,
        }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const formErrors = validatePatientForm(patientData);
        setErrors(formErrors);

        if (Object.keys(formErrors).length === 0) {
            setLoading(true)


            axios.post(`${MAIN_URL}patients`, patientData)
                .then(() => {
                    setSnackbarMessage("Data Added successfully!");
                    setSnackbarSeverity("success");
                    setOpenSnackbar(true); // Show Snackbar on success

                    setOpenDialog(true)
                    setLoading(false)
                })
                .catch((error) => {
                    console.error("Error submitting form:", error);
                    setSnackbarMessage(error?.response?.data?.message ? error.response.data.message : "Error submitting form. Please try again.");
                    setSnackbarSeverity("error");
                    setOpenSnackbar(true); // Show Snackbar on error
                    setLoading(false);
                });

        } else {
            setSnackbarMessage("Form Not Filled Properly , Please Check and Try Again");
            setSnackbarSeverity("error");
            setOpenSnackbar(true); // Show Snackbar on success

            // Scroll to the top so the first section's error is visible
            // instead of leaving the admin staring at whichever section
            // they happened to be filling in.
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    return (
        <ThemeProvider theme={customTheme}>
            <CssBaseline enableColorScheme />
            <Box sx={{ bgcolor: 'grey.50', minHeight: '100vh', pb: 8 }}>

                {/* Hero header */}
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, hsl(210,98%,42%) 0%, hsl(210,98%,32%) 100%)',
                        color: '#fff',
                        py: { xs: 4, sm: 5 },
                        px: { xs: 2.5, sm: 5 },
                        mb: { xs: 3, sm: 4 },
                    }}
                >
                    <Box sx={{ maxWidth: 960, mx: 'auto' }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.15)', width: 56, height: 56 }}>
                                <PersonAddAlt1RoundedIcon fontSize="medium" />
                            </Avatar>
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                    Add New Patient
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                                    Fill in the patient's details to create their health profile
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                </Box>

                <Box sx={{ maxWidth: 960, mx: 'auto', px: { xs: 2, sm: 3 } }}>
                    <form onSubmit={(e) => e.preventDefault()}>
                        <Stack spacing={3}>
                            <PatientFormFields
                                patientData={patientData}
                                errors={errors}
                                onInputChange={handleInputChange}
                                onChoiceChange={setField}
                                onMultiChoiceChange={setMultiField}
                            />

                            {/* Submit */}
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: { xs: 2, sm: 2.5 },
                                    borderRadius: 3,
                                    borderColor: 'divider',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
                                    <NotesRoundedIcon fontSize="small" />
                                    <Typography variant="body2">
                                        Double-check the details above before submitting.
                                    </Typography>
                                </Stack>
                                <Button
                                    type="button"
                                    onClick={handleSubmit}
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    disabled={loading}
                                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <PersonAddAlt1RoundedIcon />}
                                    sx={{ borderRadius: 2, px: 4, textTransform: 'none', fontWeight: 600 }}
                                >
                                    {loading ? "Submitting..." : "Add Patient"}
                                </Button>
                            </Paper>
                        </Stack>
                    </form>
                </Box>

                {/* Snackbar for success/error messages */}
                <Snackbar
                    open={openSnackbar}
                    autoHideDuration={4000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbarSeverity}
                        variant="filled"
                        sx={{ fontWeight: 600 }}
                    >
                        {snackbarMessage}
                    </Alert>
                </Snackbar>

                <Dialog open={openDialog} PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
                    <DialogContent sx={{ textAlign: 'center', pt: 4 }}>
                        <CheckCircleRoundedIcon sx={{ fontSize: 56, color: 'success.main', mb: 1 }} />
                        <DialogTitle sx={{ p: 0, fontWeight: 700 }}>Patient Added</DialogTitle>
                        <Typography color="text.secondary" sx={{ mt: 1 }}>
                            The patient's profile has been created successfully.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                        <Button
                            onClick={() => {
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                                setTimeout(() => {
                                    window.location.reload();
                                }, 2000); // Adjust the time to match the scroll duration
                            }}
                            variant="contained"
                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                        >
                            Add Another Patient
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </ThemeProvider>
    );
}
